import React, { useState, useEffect, useMemo } from "react";
import { Table, InputGroup, Form, Row, Col, Badge, Spinner, Button } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import AnimationLoading from "../../components/Animationloading";
import { LINEAR_GRADIENT_BG, USER_KEY } from "../../const";
import { useLazyQuery } from "@apollo/client";
import { GET_INVENTORIES, GET_SURVIVAL_LOTS } from "../../apollo/inventory/query";
import EmptyComponent from "../../components/EmptyComponent";
// import CustomButton from "../../components/CustomButton";
import PaginationHelper from "../../components/PaginationHelper";
import { convertCurrency, covertTimeZone, numberFormat } from "../../helper";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import { MdOutlineDateRange } from "react-icons/md";
import { useTranslation } from "react-i18next";

export default function InventoryList(props) {
  const { t } = useTranslation();
  const { _limit, _skip, Pagination_helper } = PaginationHelper();
  // GraphQL Queries
  const [getInventories, { data: inventoriesData, loading: loadingInventories }] = 
    useLazyQuery(GET_INVENTORIES, { fetchPolicy: "cache-and-network" });
  const [getSurvivalLots, { data: lotsData, loading: loadingLots }] = 
    useLazyQuery(GET_SURVIVAL_LOTS, { fetchPolicy: "cache-first" });

  // State
  const [searchText, setSearchText] = useState("");
  const [selectedLot, setSelectedLot] = useState("ALL");
  const [expiryFilter, setExpiryFilter] = useState("ALL"); // all, expired, notExpired
  const [sortBy, setSortBy] = useState("createdAt_DESC");
  // * State select date
  const [isCurrentModals, setIsCurrentModals] = useState(false);
  const [activeButton, setActiveButton] = useState("");
  const [filter, setFilter] = useState({
    createdAt_gte: '',
    createdAt_lt: '',
  });

  // Filter options data
  const expiryFilterOptions = [
    { value: "ALL", label: t("inv_expiry_all"), group: null },
    { value: "NOT_EXPIRED", label: t("inv_expiry_not_expired"), group: null },
    { 
      group: t("inv_expiry_expired_group"),
      options: [
        { value: "EXPIRED", label: t("inv_expiry_expired_all") },
        { value: "LAST_MONTH", label: t("inv_expiry_expired_last_month") },
        { value: "LAST_WEEK", label: t("inv_expiry_expired_last_week") },
        { value: "YESTERDAY", label: t("inv_expiry_expired_yesterday") },
        { value: "TODAY", label: t("inv_expiry_expired_today") }
      ]
    },
    {
      group: t("inv_expiry_near_expired_group"),
      options: [
        { value: "TOMORROW", label: t("inv_expiry_near_expired_tomorrow") },
        { value: "NEXT_WEEK", label: t("inv_expiry_near_expired_next_week") },
        { value: "NEXT_30DAYS", label: t("inv_expiry_near_expired_next_30days") },
        { value: "NEXT_2MONTH", label: t("inv_expiry_near_expired_next_2month") },
        { value: "NEXT_3MONTH", label: t("inv_expiry_near_expired_next_3month") }
      ]
    },
    { value: "NOT_EXPIRED", label: t("inv_expiry_not_expired_label"), group: null }
  ];

  const sortOptions = [
    { value: "createdAt_DESC", label: t("inv_sort_latest") },
    { value: "createdAt_ASC", label: t("inv_sort_oldest") },
    { value: "name_ASC", label: t("inv_sort_az") },
    { value: "name_DESC", label: t("inv_sort_za") },
    { value: "amount_DESC", label: t("inv_sort_amount_desc") },
    { value: "amount_ASC", label: t("inv_sort_amount_asc") },
    { value: "exp_ASC", label: t("inv_sort_exp_asc") },
    { value: "exp_DESC", label: t("inv_sort_exp_desc") },
    { value: "mfg_ASC", label: t("inv_sort_mfg_asc") },
    { value: "mfg_DESC", label: t("inv_sort_mfg_desc") }
  ];

  // Memoized data
  const lots = useMemo(() => {
    if (lotsData?.survivals?.data) {
      return lotsData?.survivals?.data || [];
    }
    return [];
  }, [lotsData]);

  const inventories = useMemo(() => {
    try {
      if (inventoriesData?.inventories) {
        const parsedData = inventoriesData.inventories;
        
        if (parsedData?.status) {
          props.setIsTitle(`${t("inv_list_lot_prefix")}(${parsedData?.total || 0})`);
          return parsedData || null;
        }
      }
      return null;
    } catch (error) {
      console.error("Error parsing inventory data:", error);
      return null;
    }
  }, [inventoriesData, props]);
  
  const selectDate = useMemo(() => {
    return props.selectDate;
  }, [props.selectDate]);

  // Effects
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchInventories(signal),
          fetchLots(signal)
        ]);
      } catch (error) {
        if (!error.name === 'AbortError') {
          console.error('Fetch error:', error);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    if (signal.aborted) return;
    
    const timer = setTimeout(() => {
      fetchInventories(signal);
    }, 100); // Debounce search
    
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchText, selectedLot, expiryFilter, _skip, _limit, sortBy, filter, selectDate]);

  // Fetch functions
  async function fetchInventories(signal) {
    try {
      if (signal.aborted) return;
      
      const userData = localStorage.getItem(USER_KEY);
      const locals = JSON.parse(userData);
      if (!locals?.data?.shop?.id) return;
      
      let where = {
        shops: [locals?.data?.shop?.id],
        isDeleted: false,
        expMsg: expiryFilter
      };
      
      // Apply lot filter
      if (selectedLot && selectedLot !== "ALL") {
        where = { ...where, lot: selectedLot };
      }
      
      // Apply search filter
      if (searchText.trim() !== "") {
        where = { ...where, search: searchText.trim() };
      }

      // Apply createdAt filter
      if (filter?.createdAt_gte !== "" && filter?.createdAt_lt !== "") {
        if (selectDate === "createdAt") {
          where = {
            ...where,
            createdAt_gte: filter?.createdAt_gte,
            createdAt_lt: filter?.createdAt_lt
          };
        } else if (selectDate === "mfg") {
          where = {
            ...where,
            mfg_gte: filter?.createdAt_gte,
            mfg_lt: filter?.createdAt_lt
          };
        } else if (selectDate === "exp") {
          where = {
            ...where,
            exp_gte: filter?.createdAt_gte,
            exp_lt: filter?.createdAt_lt
          };
        }
      }
      
      await getInventories({
        variables: {
          limit: _limit,
          skip: _skip - 1,
          orderBy: sortBy,
          where
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      console.error("Error fetching inventories:", error);
    }
  }

  async function fetchLots(signal) {
    try {
      if (signal.aborted || loadingLots) return;
      
      const userData = localStorage.getItem(USER_KEY);
      const locals = JSON.parse(userData);
      if (!locals?.data?.shop?.id) return;
      
      await getSurvivalLots({
        variables: {
          where: {
            shop: locals?.data?.shop?.id,
            status: true,
            isDeleted: false
          },
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      console.error("Error fetching lots:", error);
    }
  }

  // Render helper functions
  const renderExpiryStatus = (expDate) => {
    if (!expDate) return <Badge bg="secondary">{t("inv_no_exp")}</Badge>;
    
    const now = moment();
    const exp = moment(expDate);
    const daysUntilExpiry = exp.diff(now, 'days');
    
    if (daysUntilExpiry < 0) {
      return <Badge bg="danger">{t("inv_expired_badge")}</Badge>;
    } else if (daysUntilExpiry <= 30) {
      return <Badge bg="warning" text="dark">{t("inv_near_exp_badge")} ({daysUntilExpiry} {t("inv_days")})</Badge>;
    } else {
      return <Badge bg="success">{t("inv_usable_badge")}</Badge>;
    }
  };

  return (
    <div className="inventory-list-container">
      {/* Removed BreadCrumb since it's now in the parent component */}
      
      {/* Filters */}
      <div className="inventory-list-filters" style={{ padding: "1em" }}>
        <Row>
          {/* Search filter enter */}
          <Col md={3}>
            <InputGroup >
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                placeholder={t("inv_ph_search")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ borderLeft: "none" }}
              />
            </InputGroup>
          </Col>
          {/* Lot filter select */}
          <Col md="auto">
            <Form.Select
              value={selectedLot}
              onChange={(e) => setSelectedLot(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              <option value="ALL">{t("inv_all_lots")}</option>
              {loadingLots ? (
                <option disabled>{t("inv_loading")}</option>
              ) : (
                lots.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.survivalNumber}
                  </option>
                ))
              )}
            </Form.Select>
          </Col>
          {/* Expired filter select */}
          <Col md="auto">
            <Form.Select
              value={expiryFilter}
              onChange={(e) => setExpiryFilter(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              {expiryFilterOptions.map((option, index) => (
                <React.Fragment key={index}>
                  {option?.group && (
                    <optgroup label={option?.group}>
                      {option?.options.map((subOption) => (
                        <option key={subOption?.value} value={subOption?.value}>
                          {subOption?.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {!option?.group && (
                    <option value={option?.value}>{option?.label}</option>
                  )}
                </React.Fragment>
              ))}
            </Form.Select>
          </Col>
          {/* SortBy filter select */}
          <Col md="auto">
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              {sortOptions.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          {/* Date filter buttons */}
          <Col md={3}>
            <Form.Group>
              <Button
                variant="outline-secondary"
                onClick={() => setIsCurrentModals(true)}
                style={{ textAlign: "left", marginTop: '-.2em', width: '100%' }}
              >
                {(loadingLots) ? (
                  <Spinner size="sm" />
                ) : (
                  <MdOutlineDateRange style={{ fontSize: 24 }} />
                )}
                &nbsp;  &nbsp;
                {(filter?.createdAt_gte === "" && filter?.createdAt_lt === "") ? (
                  <span style={{ fontSize: 14 }}>{t("pm_search_by_date")}</span>
                ) : (
                  <span style={{ fontSize: 14 }}>
                    {covertTimeZone(filter?.createdAt_gte) +
                      " - " +
                      covertTimeZone(filter?.createdAt_lt)}
                  </span>
                )}
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </div>
      
      {/* Table */}
      <div className="inventory-list-table" style={{ padding: "0 1em" }}>
        {inventories?.dataMany?.total === 0 || loadingInventories ? (
          <>
            {loadingInventories ? <AnimationLoading /> : <EmptyComponent title={t("inv_no_data")} />}
          </>
        ) : (
          <Table 
            responsive
            style={{
              marginTop: "10px",
              borderCollapse: "collapse",
              width: "100%",
              background: "#fff"
            }}
          >
            <thead
              style={{
                background: LINEAR_GRADIENT_BG,
                borderRadius: "8px 8px 0px 0px",
                color: "#FFFFFF",
              }}
            >
              <tr>
                <th style={{ width: "5%" }}>{t("inv_th_no")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_lot")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_name")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_category")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_amount")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_unit")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_price")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_date")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_mfg")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_exp")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_status")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_by")}</th>
              </tr>
            </thead>
            <tbody>
              {inventories?.dataMany?.map((item, index) => (
                <tr key={item?._id}>
                  <td><Badge bg="light" text="dark">{(_skip - 1) + index + 1}</Badge></td>
                  <td><Badge bg="light" text="dark">{item?.lot?.survivalNumber || "-"}</Badge></td>
                  <td><Badge bg="light" text="dark">{item?.name}</Badge></td>
                  <td><Badge bg="light" text="dark">{item?.categoryName || item?.category?.name || "-"}</Badge></td>
                  <td><Badge bg="light" text="dark">{item?.amount}</Badge></td>
                  <td><Badge bg="light" text="dark">{item?.unitName || item?.unitId?.name || item?.unit || "-"}</Badge></td>
                  <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span>{convertCurrency(item?.currency || "LAK")}</span>
                      <span>{numberFormat(item?.price || 0)}</span>
                      {item?.shippingCost > 0 && (
                        <span style={{ color: "#666", fontSize: "0.7em" }}>
                          (+{numberFormat(item?.shippingCost)})
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <Badge bg="info">{item?.createdAt ? moment(item?.createdAt).format("DD/MM/YYYY HH:mm") : "-"}</Badge>
                  </td>
                  <td>
                    <Badge bg="info">{item?.mfg ? moment(item?.mfg).format("DD/MM/YYYY") : "-"}</Badge>
                  </td>
                  <td>
                    <Badge bg="info">{item?.exp ? moment(item?.exp).format("DD/MM/YYYY") : "-"}</Badge>
                  </td>
                  <td>{renderExpiryStatus(item?.exp)}</td>
                  <td>
                    <Badge bg="secondary">{item?.createdBy.firstName || "-"}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        
        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3 mb-5">
          {Pagination_helper(inventories?.total || 0)}
        </div>
      </div>
      {/* //TODO Modal select date lenght */}
      <CurrentDateComponents
        onCloseModal={() => setIsCurrentModals(false)}
        openModal={isCurrentModals}
        setCdFillter={setFilter}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
      />
    </div>
  );
}
